/**
 * usePreloadStore — Reactive video blob cache for components.
 *
 * The PreloadManager handles all fetching and queueing. This store exists
 * solely to give React components reactive access to cached video blob URLs
 * (since the PreloadManager is a plain class, not reactive).
 *
 * Hooks interact with preloadManager directly for enqueuing/promoting/aborting.
 * Components use this store only for getVideoSrc and revokeVideo.
 */

import { create } from "zustand";
import { preloadManager } from "./PreloadManager";

interface PreloadState {
    /** Video blob URL cache: original URL → blob URL */
    videoCache: Map<string, string>;

    /** Get cached video blob URL, falling back to original */
    getVideoSrc: (originalSrc: string) => string;

    /** Revoke a video blob URL to free memory */
    revokeVideo: (originalSrc: string) => void;
}

// ---------------------------------------------------------------------------
// Wire up the video blob handler before store creation
// ---------------------------------------------------------------------------

const videoCache = new Map<string, string>();

preloadManager.setVideoBlobHandler((originalUrl, blobUrl) => {
    videoCache.set(originalUrl, blobUrl);
    // Trigger store update so components re-render with new blob URL
    usePreloadStore.setState({ videoCache: new Map(videoCache) });
});

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const usePreloadStore = create<PreloadState>((set, get) => ({
    videoCache,

    getVideoSrc: (originalSrc) => {
        return get().videoCache.get(originalSrc) ?? originalSrc;
    },

    revokeVideo: (originalSrc) => {
        const blobUrl = get().videoCache.get(originalSrc);
        if (blobUrl) {
            URL.revokeObjectURL(blobUrl);
            const next = new Map(get().videoCache);
            next.delete(originalSrc);
            set({ videoCache: next });
        }
    },
}));
